// Carrito y confirmación (documento 20 y decisión D49), como en una tienda online: "Tu carrito" con cada producto (foto,
// precio por unidad, − cantidad +, papelera y subtotal), "¿Cómo querés pagar?", la entrega, una nota, el total y
// "Confirmar pedido".
// - Con sesión: lo del celular se revisa con la API (`POST /tienda/carrito`) a cada cambio (precios y stock de hoy,
//   mínimo, formas de pago y fecha). "Confirmar" manda una clave por intento y la reusa si se reintenta.
// - Sin cuenta: el carrito del visitante, con los precios que vio. "Confirmar pedido" lleva a entrar con la cuenta (o a
//   crearla; decisión D51) y vuelve acá con todo lo elegido; si los precios de su lista y el stock no cambiaron, se confirma
//   solo; si cambió algo, se avisa claro antes.
// - En modo vendedor (decisión D60), sin señal el pedido queda guardado con su clave y la hora, y se manda solo cuando
//   vuelve (datos/vendedor.ts).
import { formatoGs, MEDIO_COBRO_ETIQUETA, type MedioCobro, uuidv7 } from '../compartido';
import { cx, EsqueletoEnLinea, HojaInferior, Icono } from '@reparto/ui';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Aviso, Boton, TituloSeccion, Vacio } from '../componentes/base';
import { ControlCantidad } from '../componentes/Cantidad';
import { Pantalla } from '../componentes/Marco';
import { FotoProducto } from '../componentes/TarjetaProducto';
import type { DesdePedido } from '../componentes/VolverAPedir';
import { ErrorApi, mensajeDeError, SinRed } from '../datos/api';
import {
  arreglarTodo,
  arregloDe,
  type CambioPrecio,
  cambiosDePrecio,
  completarConRevision,
  fijarBultos,
  fijarOpciones,
  itemsParaApi,
  type LineaCarrito,
  quitar,
  resumen,
} from '../datos/carrito';
import { escribirCache, olvidarConsultas } from '../datos/consultas';
import { useCuenta, useNegocio } from '../datos/cuenta';
import { aceptaRegistros } from '../datos/entrada';
import { cambiarCarrito, olvidarUnion, useCarrito, vaciarCarrito } from '../datos/estado-carrito';
import { cantidadEnUnidad, fechaEntrega, nombreUnidad, plural, presentacion } from '../datos/formato';
import { useMarca } from '../datos/marca';
import { confirmarPedido, revisarCarrito } from '../datos/pedidos';
import { useSesion, useVendedor } from '../datos/sesion';
import { guardarPedido } from '../datos/vendedor';
import type { LineaRevisada, Revision } from '../datos/tipos';
import { recordarVolver } from '../datos/volver';

const ICONO_MEDIO: Record<MedioCobro, string> = {
  efectivo: 'payments',
  qr: 'qr_code_2',
  transferencia: 'account_balance',
  cuenta_corriente: 'menu_book',
};
const AYUDA_MEDIO: Record<MedioCobro, string> = {
  efectivo: 'Le pagás al chofer cuando llega',
  qr: 'Escaneás el QR cuando llega',
  transferencia: 'Transferís cuando llega',
  cuenta_corriente: 'Se anota en tu cuenta',
};

/** Problemas que no dependen de la cantidad: valen aunque la revisión sea de una cantidad anterior. */
const FIJOS = new Set(['no_existe', 'inactivo', 'sin_precio', 'sin_stock']);

function problemaVigente(l: LineaCarrito, v: LineaRevisada | undefined): LineaRevisada['problema'] {
  if (!v?.problema) return null;
  return FIJOS.has(v.problema) || v.bultos === l.bultos ? v.problema : null;
}

/** Lo que trae la navegación al carrito: desde un pedido anterior o al volver de entrar (confirmar solo). */
type EstadoLlegada = Partial<DesdePedido> & { confirmar?: boolean };

export function Carrito() {
  const conSesion = useSesion((s) => !!s.sesion);
  return conSesion ? <CarritoCliente /> : <CarritoVisitante />;
}

function CarritoVacio({ subtitulo }: { subtitulo?: string }) {
  const navegar = useNavigate();
  return (
    <Pantalla titulo="Tu carrito" subtitulo={subtitulo}>
      <Vacio icono="shopping_cart" titulo="Tu carrito está vacío" texto="Agregá productos desde el catálogo.">
        <Boton tamano="lg" icono="storefront" onClick={() => navegar('/')}>
          Ver productos
        </Boton>
      </Vacio>
    </Pantalla>
  );
}

// ---------- Con sesión ----------

function CarritoCliente() {
  const perfil = useCuenta((s) => s.perfil)!;
  const negocio = useNegocio()!;
  const carrito = useCarrito((s) => s.carrito);
  const unido = useCarrito((s) => s.unido);
  const navegar = useNavigate();
  const { state } = useLocation() as { state: EstadoLlegada | null };
  const reglas = perfil.tienda;
  const vendedor = useVendedor();
  // El preventista: lo que toma pasa por Pedidos o se aprueba solo, según la configuración de la preventa.
  const aprobacion = vendedor
    ? perfil.preventa?.aprobacion === 'automatica'
      ? 'automatica'
      : 'manual'
    : reglas.aprobacion;

  const items = useMemo(() => itemsParaApi(carrito), [carrito]);
  const firma = JSON.stringify(items);
  const [revision, setRevision] = useState<{ r: Revision; firma: string } | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [errorRevision, setErrorRevision] = useState<{ mensaje: string; sinRed: boolean } | null>(null);
  const [intento, setIntento] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [errorConfirmar, setErrorConfirmar] = useState<string | null>(null);
  const [vaciar, setVaciar] = useState(false);
  // Al volver de entrar se confirma solo si nada cambió; si no, se avisa qué mirar.
  const [alEntrar] = useState(() => !!state?.confirmar || !!unido);
  const autoConfirmar = useRef(!!state?.confirmar);
  const [noSeConfirmoSolo, setNoSeConfirmoSolo] = useState(false);

  // Lo elegido sin cuenta ya pasó al carrito del negocio: el aviso se ve esta vez.
  useEffect(() => () => olvidarUnion(), []);

  // Revisión con espera corta: tocar + varias veces seguidas hace una sola consulta.
  useEffect(() => {
    if (!items.length) {
      setRevision(null);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(
      async () => {
        setRevisando(true);
        try {
          const r = await revisarCarrito(negocio.id, items, ctrl.signal);
          setRevision({ r, firma });
          setErrorRevision(null);
          cambiarCarrito((c) => completarConRevision(c, r));
        } catch (e) {
          if (ctrl.signal.aborted) return;
          setErrorRevision({ mensaje: mensajeDeError(e), sinRed: e instanceof SinRed });
        } finally {
          if (!ctrl.signal.aborted) setRevisando(false);
        }
      },
      revision ? 450 : 0,
    );
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
    // `items` cambia con `firma`; la revisión anterior solo decide la espera.
  }, [firma, negocio.id, intento]);

  // Cambió el pedido: el error del intento anterior ya no corresponde.
  useEffect(() => setErrorConfirmar(null), [firma]);

  const r = revision?.r ?? null;
  const fresca = !!revision && revision.firma === firma;
  const porId = useMemo(() => new Map(r?.lineas.map((l) => [l.articulo_id, l]) ?? []), [r]);
  const cambios = useMemo(() => (r ? cambiosDePrecio(carrito, r) : new Map<string, CambioPrecio>()), [carrito, r]);
  const conProblema = carrito.lineas.filter((l) => problemaVigente(l, porId.get(l.articulo_id)));
  const local = resumen(carrito);
  const monto = reglas.mostrar_precio ? (fresca ? r!.monto : local.monto) : null;

  const fechas = r?.fechas_entrega ?? reglas.fechas_entrega;
  const fecha =
    carrito.opciones.fecha_entrega && fechas.includes(carrito.opciones.fecha_entrega)
      ? carrito.opciones.fecha_entrega
      : fechas[0];
  const medios = r?.medios_pago ?? negocio.medios_pago;
  const cobro: MedioCobro | null =
    carrito.opciones.cobro && medios.includes(carrito.opciones.cobro)
      ? carrito.opciones.cobro
      : (r?.cobro_default ?? negocio.cobro_default ?? medios[0] ?? null);

  // Por qué no se puede confirmar todavía (null: se puede).
  let motivo: string | null = null;
  if (fresca && !r!.se_puede_confirmar) {
    if (conProblema.length) motivo = 'Arreglá lo marcado en rojo para poder confirmar.';
    else if (!r!.alcanza_minimo)
      motivo =
        r!.falta_para_minimo !== null
          ? `Te faltan ${formatoGs(r!.falta_para_minimo)} para el pedido mínimo.`
          : `El pedido todavía no llega al mínimo de ${formatoGs(r!.monto_minimo)}.`;
    else if (!r!.medios_pago.length)
      motivo = 'Tu negocio no tiene formas de pago habilitadas. Consultá con la distribuidora.';
    else motivo = 'Revisá el pedido.';
  } else if (!fresca && !errorRevision) motivo = 'Revisando precios y stock…';
  // Sin señal para revisar se deja intentar igual: si vuelve la señal, la API revisa todo al confirmar.

  async function confirmar() {
    setErrorConfirmar(null);
    let clave = carrito.clave;
    if (!clave) {
      clave = uuidv7();
      const nueva = clave;
      cambiarCarrito((c) => ({ ...c, clave: nueva }));
    }
    setEnviando(true);
    const nuevo = {
      items,
      cobro,
      fecha_entrega: reglas.elegir_fecha ? fecha : null,
      observaciones: carrito.opciones.observaciones,
      clave,
      ...(vendedor && { hora: new Date().toISOString() }),
    };
    try {
      const p = await confirmarPedido(negocio.id, nuevo);
      escribirCache(`pedido:${p.id}`, p);
      olvidarConsultas(`pedidos:${negocio.id}`);
      olvidarConsultas(`frecuentes:${negocio.id}`);
      navegar(`/confirmado/${p.id}`, { replace: true });
      vaciarCarrito();
    } catch (e) {
      setEnviando(false);
      // El preventista no espera la señal en el local: queda guardado y sigue con su recorrido.
      if (e instanceof SinRed && vendedor) {
        guardarPedido(negocio, nuevo, { bultos: local.bultos, monto });
        vaciarCarrito();
        return navegar(`/visitas/${negocio.id}`, { replace: true, state: { pedidoGuardado: true } });
      }
      if (e instanceof SinRed)
        return setErrorConfirmar(
          'No hay señal. Tu pedido sigue guardado: tocá "Confirmar" de nuevo cuando tengas señal.',
        );
      if (e instanceof ErrorApi) {
        if (e.codigo === 'VALIDACION' && (e.detalles as { fechas_entrega?: string[] } | undefined)?.fechas_entrega)
          cambiarCarrito((c) => fijarOpciones(c, { fecha_entrega: null }));
        // Algo cambió (stock, precio, mínimo, fecha, forma de pago): se revisa de nuevo para mostrarlo.
        if (e.estado === 422 || e.estado === 400) setIntento((n) => n + 1);
      }
      setErrorConfirmar(mensajeDeError(e));
    }
  }

  const nProblemas = conProblema.length;
  const nCambios = cambios.size;
  const pendiente = negocio.estado_tienda === 'pendiente';

  // Volvió de entrar para confirmar: si todo sigue igual, se confirma solo; si no, se queda acá mostrando qué cambió.
  useEffect(() => {
    if (!autoConfirmar.current || !fresca || enviando) return;
    autoConfirmar.current = false;
    if (!nCambios && !nProblemas && r!.se_puede_confirmar && !unido?.previos && !pendiente) void confirmar();
    else setNoSeConfirmoSolo(true);
  }, [fresca]);

  if (!carrito.lineas.length) return <CarritoVacio subtitulo={negocio.nombre} />;

  const avisoAlEntrar =
    alEntrar && fresca && !enviando ? (
      <Aviso
        tono={nCambios || nProblemas || pendiente ? 'aviso' : 'ok'}
        icono="how_to_reg"
        titulo={
          noSeConfirmoSolo || nCambios || nProblemas
            ? 'Ya entraste: revisá tu pedido antes de confirmar'
            : 'Ya entraste: tus precios están revisados'
        }
      >
        {[
          nCambios
            ? `${plural(nCambios, 'producto tiene', 'productos tienen')} otro precio en tu lista (lo marcamos abajo).`
            : 'Los precios de tu lista son los mismos que viste.',
          nProblemas ? `${plural(nProblemas, 'producto no está', 'productos no están')} para tu negocio.` : null,
          unido?.previos
            ? `También quedó lo que tenías guardado de antes (${plural(unido.previos, 'producto', 'productos')}).`
            : null,
          pendiente
            ? 'Tu cuenta está en revisión: tu pedido queda guardado y lo confirmás cuando la distribuidora la apruebe.'
            : null,
        ]
          .filter(Boolean)
          .join(' ')}
      </Aviso>
    ) : null;

  // Por qué todavía no se puede confirmar, o qué pasó al intentarlo.
  const estado = errorConfirmar ? (
    <p role="alert" className="mb-2 flex gap-2 px-1 text-[15px] font-medium text-rose-800">
      <Icono nombre="error" tamano={20} className="mt-px shrink-0" />
      {errorConfirmar}
    </p>
  ) : motivo ? (
    <p className="mb-2 px-1 text-[15px] text-stone-700" aria-live="polite">
      {motivo}
    </p>
  ) : null;
  const botonConfirmar = (bloque: boolean) => (
    <Boton
      bloque={bloque}
      tamano="lg"
      icono="check"
      cargando={enviando}
      disabled={!!motivo}
      onClick={() => void confirmar()}
      className={cx('shrink-0', bloque && 'h-15! text-lg!')}
    >
      {enviando ? 'Enviando…' : 'Confirmar pedido'}
    </Boton>
  );
  const totales = (
    <Totales
      articulos={local.articulos}
      bultos={local.bultos}
      monto={reglas.mostrar_precio ? (fresca || monto !== null ? monto : undefined) : null}
    >
      {fresca && !r!.alcanza_minimo ? (
        <Aviso
          tono="aviso"
          icono="savings"
          className="mt-3"
          titulo={
            r!.falta_para_minimo !== null
              ? `Te faltan ${formatoGs(r!.falta_para_minimo)} para el mínimo`
              : 'Todavía no llega al pedido mínimo'
          }
          accion={
            <Boton variante="secundario" tamano="sm" className="h-11!" icono="add" onClick={() => navegar('/')}>
              Agregar más
            </Boton>
          }
        >
          El pedido mínimo es de {formatoGs(r!.monto_minimo)}.
        </Aviso>
      ) : null}
      <div className="mt-3">
        {vendedor ? (
          <Aviso tono="info" icono={aprobacion === 'manual' ? 'fact_check' : 'bolt'}>
            {aprobacion === 'manual'
              ? 'El pedido pasa por Pedidos de la distribuidora antes de prepararse. El cliente lo ve en su tienda.'
              : 'Al confirmar, el pedido pasa directo a preparación. El cliente lo ve en su tienda.'}
          </Aviso>
        ) : aprobacion === 'manual' ? (
          <Aviso tono="info" icono="fact_check">
            La distribuidora revisa tu pedido y te lo confirma. Vas a ver cómo va en <b>Mis pedidos</b>.
          </Aviso>
        ) : (
          <Aviso tono="info" icono="bolt">
            Al confirmar, tu pedido pasa directo a preparación.
          </Aviso>
        )}
      </div>
    </Totales>
  );

  return (
    <Pantalla
      titulo="Tu carrito"
      subtitulo={negocio.nombre}
      amplia
      pieSoloCelular
      pie={
        // Una sola fila (total y botón) para tapar lo menos posible de lo que hay arriba.
        <div className="rounded-3xl border border-stone-200 bg-white p-3 shadow-lg shadow-black/20">
          {estado}
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1 px-1 leading-tight whitespace-nowrap">
              {monto !== null && monto > 0 ? (
                <p className="text-lg font-bold tabular-nums">{formatoGs(monto)}</p>
              ) : null}
              <p className="truncate text-[15px] text-stone-600">{plural(local.bultos, 'bulto', 'bultos')}</p>
            </div>
            {botonConfirmar(false)}
          </div>
        </div>
      }
    >
      <Columnas
        lateral={
          <>
            {totales}
            <div className="mt-3 rounded-3xl border border-stone-200 bg-white shadow-sm p-3">
              {estado}
              {botonConfirmar(true)}
            </div>
          </>
        }
      >
        <div className="flex flex-col gap-3 px-4 pt-4">
          {avisoAlEntrar}
          {enviando && alEntrar ? (
            <Aviso tono="info" icono="progress_activity" titulo="Confirmando tu pedido…">
              Revisamos precios y stock con tu lista: está todo igual.
            </Aviso>
          ) : null}
          {state?.desde && fresca ? (
            nProblemas || nCambios ? (
              <Aviso tono="aviso" icono="history" titulo={`Cargamos lo de ${state.desde}, pero hay cambios`}>
                {[
                  nCambios ? `${plural(nCambios, 'producto cambió', 'productos cambiaron')} de precio` : null,
                  nProblemas ? `${plural(nProblemas, 'no se puede', 'no se pueden')} pedir así` : null,
                ]
                  .filter(Boolean)
                  .join(' y ')}
                . Mirá lo marcado abajo.
              </Aviso>
            ) : (
              <Aviso tono="ok" titulo={`Cargamos lo de ${state.desde}`}>
                Revisamos precios y stock: está todo bien. Cambiá lo que quieras y confirmá.
              </Aviso>
            )
          ) : null}
          {nProblemas > 0 && fresca ? (
            <Aviso
              tono="peligro"
              titulo={`${plural(nProblemas, 'producto no se puede', 'productos no se pueden')} pedir así`}
              accion={
                <Boton
                  variante="peligro"
                  icono="auto_fix_high"
                  onClick={() => r && cambiarCarrito((c) => arreglarTodo(c, r))}
                >
                  Arreglar todo
                </Boton>
              }
            >
              Quitamos lo que no hay y dejamos la cantidad que queda.
            </Aviso>
          ) : null}
          {errorRevision ? (
            <Aviso
              tono={errorRevision.sinRed ? 'info' : 'peligro'}
              icono={errorRevision.sinRed ? 'signal_disconnected' : 'error'}
              titulo={errorRevision.sinRed ? 'Sin señal' : 'No pudimos revisar el pedido'}
              accion={
                <Boton
                  variante="secundario"
                  tamano="sm"
                  className="h-11!"
                  icono="refresh"
                  cargando={revisando}
                  onClick={() => setIntento((n) => n + 1)}
                >
                  Revisar de nuevo
                </Boton>
              }
            >
              {errorRevision.sinRed
                ? 'No pudimos revisar precios y stock. Tu pedido queda guardado en el celular.'
                : errorRevision.mensaje}
            </Aviso>
          ) : null}
        </div>

        <ListaProductos onVaciar={() => setVaciar(true)}>
          {carrito.lineas.map((l) => (
            <FilaCarrito
              key={l.articulo_id}
              l={l}
              v={porId.get(l.articulo_id)}
              problema={problemaVigente(l, porId.get(l.articulo_id))}
              cambio={cambios.get(l.articulo_id)}
              mostrarPrecio={reglas.mostrar_precio}
              permitirSinStock={reglas.permitir_sin_stock}
            />
          ))}
        </ListaProductos>

        <section className="px-4 pt-5" aria-labelledby="pago">
          <ElegirPago medios={medios} valor={cobro} />
        </section>

        <section className="px-4 pt-5" aria-labelledby="entrega">
          <TituloSeccion id="entrega">Entrega</TituloSeccion>
          {reglas.elegir_fecha && fechas.length > 1 ? (
            <Opciones
              nombre="fecha"
              valor={fecha ?? null}
              onCambiar={(f) => cambiarCarrito((c) => fijarOpciones(c, { fecha_entrega: f }))}
              opciones={fechas.map((f, i) => ({
                valor: f,
                titulo: primeraMayuscula(fechaEntrega(f, perfil.empresa.zona_horaria)),
                ayuda: i === 0 ? 'Lo antes posible' : undefined,
                icono: 'event',
              }))}
            />
          ) : fecha ? (
            <Llegada fecha={fechaEntrega(fecha, perfil.empresa.zona_horaria)} direccion={negocio.direccion} />
          ) : null}
        </section>

        <section className="px-4 pt-5" aria-labelledby="observaciones">
          <Observaciones
            valor={carrito.opciones.observaciones}
            onCambiar={(o) => cambiarCarrito((c) => fijarOpciones(c, { observaciones: o }))}
          />
        </section>

        <section aria-label="Total" className="px-4 pt-5 pb-4 lg:hidden">
          {totales}
        </section>
      </Columnas>

      <HojaVaciar abierta={vaciar} onCerrar={() => setVaciar(false)} />
    </Pantalla>
  );
}

// ---------- Sin cuenta (decisión D49) ----------

function CarritoVisitante() {
  const marca = useMarca((s) => s.marca);
  const carrito = useCarrito((s) => s.carrito);
  const navegar = useNavigate();
  const [vaciar, setVaciar] = useState(false);
  const c = marca?.catalogo;
  const mostrarPrecio = !!c && c.publico === 'con_precios' && c.mostrar_precio;
  const medios = c?.medios_pago ?? [];
  const cobro = carrito.opciones.cobro && medios.includes(carrito.opciones.cobro) ? carrito.opciones.cobro : medios[0];
  const local = resumen(carrito);
  const nuevos = aceptaRegistros(marca?.acceso);
  const nombre = marca?.nombre ?? 'la distribuidora';
  if (!carrito.lineas.length) return <CarritoVacio />;

  const confirmar = () => {
    recordarVolver('/carrito', { confirmar: true });
    navegar('/entrar', { state: { paraConfirmar: true } });
  };
  const faltaMinimo = mostrarPrecio && c && c.monto_minimo > 0 && (local.monto ?? 0) < c.monto_minimo;
  const ayuda = (
    <p className="mb-2 px-1 text-[15px] text-stone-700">
      {nuevos
        ? 'Para confirmar entrás a tu cuenta. Si es tu primera vez, la creás en un minuto y volvés acá.'
        : `Para confirmar entrá a tu cuenta. Si todavía no tenés, pedile a ${nombre} que te dé acceso con tu correo.`}
    </p>
  );
  const boton = (bloque: boolean) => (
    <Boton
      bloque={bloque}
      tamano="lg"
      icono={nuevos ? 'check' : 'login'}
      onClick={confirmar}
      className={cx('shrink-0', bloque && 'h-15! text-lg!')}
    >
      {nuevos ? 'Confirmar pedido' : 'Entrar para confirmar'}
    </Boton>
  );
  const totales = (
    <Totales articulos={local.articulos} bultos={local.bultos} monto={mostrarPrecio ? local.monto : null}>
      {faltaMinimo && c ? (
        <Aviso tono="aviso" icono="savings" className="mt-3" titulo="Todavía no llega al pedido mínimo">
          El pedido mínimo es de {formatoGs(c.monto_minimo)}.
        </Aviso>
      ) : null}
      <Aviso tono="info" icono="sell" className="mt-3">
        Al entrar revisamos los precios de tu lista y el stock de hoy. Si algo cambió, te lo mostramos antes de
        confirmar.
      </Aviso>
    </Totales>
  );
  return (
    <Pantalla
      titulo="Tu carrito"
      amplia
      pieSoloCelular
      pie={
        <div className="rounded-3xl border border-stone-200 bg-white p-3 shadow-lg shadow-black/20">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1 px-1 leading-tight whitespace-nowrap">
              {mostrarPrecio && local.monto ? (
                <p className="text-lg font-bold tabular-nums">{formatoGs(local.monto)}</p>
              ) : null}
              <p className="truncate text-[15px] text-stone-600">{plural(local.bultos, 'bulto', 'bultos')}</p>
            </div>
            {boton(false)}
          </div>
        </div>
      }
    >
      <Columnas
        lateral={
          <>
            {totales}
            <div className="mt-3 rounded-3xl border border-stone-200 bg-white shadow-sm p-3">
              {ayuda}
              {boton(true)}
            </div>
          </>
        }
      >
        <ListaProductos onVaciar={() => setVaciar(true)}>
          {carrito.lineas.map((l) => (
            <FilaCarrito
              key={l.articulo_id}
              l={l}
              v={undefined}
              problema={null}
              cambio={undefined}
              mostrarPrecio={mostrarPrecio}
              permitirSinStock={c?.permitir_sin_stock ?? false}
            />
          ))}
        </ListaProductos>
        {medios.length ? (
          <section className="px-4 pt-5" aria-labelledby="pago">
            <ElegirPago medios={medios} valor={cobro ?? null} />
          </section>
        ) : null}
        {c?.fechas_entrega[0] ? (
          <section className="px-4 pt-5" aria-labelledby="entrega">
            <TituloSeccion id="entrega">Entrega</TituloSeccion>
            <Llegada fecha={`${fechaEntrega(c.fechas_entrega[0])} (si pedís hoy)`} />
          </section>
        ) : null}
        <section className="px-4 pt-5" aria-labelledby="observaciones">
          <Observaciones
            valor={carrito.opciones.observaciones}
            onCambiar={(o) => cambiarCarrito((x) => fijarOpciones(x, { observaciones: o }))}
          />
        </section>
        <section aria-label="Total" className="px-4 pt-5 pb-4 lg:hidden">
          {totales}
          <div className="mt-3">{ayuda}</div>
        </section>
      </Columnas>
      <HojaVaciar abierta={vaciar} onCerrar={() => setVaciar(false)} />
    </Pantalla>
  );
}

// ---------- Piezas ----------

/** Celular: una columna. Computadora: el pedido a la izquierda y el total con "Confirmar" a la derecha. */
function Columnas({ lateral, children }: { lateral: ReactNode; children: ReactNode }) {
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-4 lg:px-2">
      <div className="min-w-0 pb-4">{children}</div>
      <aside aria-label="Total y confirmación" className="sticky top-22 hidden px-4 pt-4 lg:block">
        {lateral}
      </aside>
    </div>
  );
}

function ListaProductos({ onVaciar, children }: { onVaciar: () => void; children: ReactNode }) {
  const navegar = useNavigate();
  return (
    <section className="px-4 pt-4" aria-labelledby="lineas">
      <h2 id="lineas" className="sr-only">
        Productos del pedido
      </h2>
      <ul className="flex flex-col gap-3">{children}</ul>
      <div className="mt-2 flex flex-wrap justify-between gap-2">
        <Boton variante="fantasma" icono="add" onClick={() => navegar('/')}>
          Agregar más productos
        </Boton>
        <Boton variante="fantasma" icono="delete_sweep" className="text-stone-600!" onClick={onVaciar}>
          Vaciar
        </Boton>
      </div>
    </section>
  );
}

function Totales({
  articulos,
  bultos,
  monto,
  children,
}: {
  articulos: number;
  bultos: number;
  /** null: la empresa no muestra precios. undefined: todavía se está revisando. */
  monto: number | null | undefined;
  children?: ReactNode;
}) {
  return (
    <>
      <div className="rounded-3xl border border-stone-200 bg-white shadow-sm p-4 text-[17px]">
        <div className="flex justify-between py-1">
          <span className="text-stone-600">Productos</span>
          <span className="font-medium tabular-nums">{articulos}</span>
        </div>
        <div className="flex justify-between py-1">
          <span className="text-stone-600">Bultos</span>
          <span className="font-medium tabular-nums">{bultos}</span>
        </div>
        {monto === null ? (
          <p className="mt-2 border-t border-stone-200 pt-3 text-[15px] text-stone-600">
            El total te lo confirma la distribuidora con la factura.
          </p>
        ) : (
          <div className="mt-2 flex items-baseline justify-between border-t border-stone-200 pt-3">
            <span className="text-lg font-bold">Total</span>
            {monto === undefined ? (
              // Mientras se revisan precios y stock: el monto en gris, del alto del renglón (nada salta al llegar).
              <span className="text-2xl font-bold">
                <EsqueletoEnLinea ancho="5.5em" />
              </span>
            ) : (
              <span className="text-2xl font-bold tabular-nums">{formatoGs(monto)}</span>
            )}
          </div>
        )}
      </div>
      {children}
    </>
  );
}

function Llegada({ fecha, direccion }: { fecha: string; direccion?: string | null }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white shadow-sm p-4">
      <Icono nombre="local_shipping" tamano={30} className="shrink-0 text-primary-700" />
      <p className="text-[17px]">
        Te llega <b>{fecha}</b>
        {direccion ? <span className="block text-[15px] text-stone-600">a {direccion}</span> : null}
      </p>
    </div>
  );
}

/** "¿Cómo querés pagar?": una tarjeta por forma de pago (al recibir). */
function ElegirPago({ medios, valor }: { medios: MedioCobro[]; valor: MedioCobro | null }) {
  return (
    <>
      <h2 id="pago" className="text-xl font-bold text-stone-900">
        ¿Cómo querés pagar?
      </h2>
      <p className="mt-0.5 mb-3 text-[15px] text-stone-600">
        Pagás <b className="font-semibold text-stone-800">cuando recibís el pedido</b>, no ahora.
      </p>
      {medios.length ? (
        <div role="radiogroup" aria-labelledby="pago" className="grid grid-cols-2 gap-2">
          {medios.map((m) => {
            const elegido = m === valor;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={elegido}
                onClick={() => cambiarCarrito((c) => fijarOpciones(c, { cobro: m }))}
                className={cx(
                  'flex min-h-20 flex-col items-start justify-center gap-0.5 rounded-2xl border-2 px-3 py-2.5 text-left transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                  elegido ? 'border-primary-600 bg-primary-50' : 'border-stone-200 bg-white hover:border-stone-300',
                )}
              >
                <span
                  className={cx(
                    'flex items-center gap-2 text-[17px] font-semibold',
                    elegido ? 'text-primary-800' : 'text-stone-900',
                  )}
                >
                  <Icono
                    nombre={ICONO_MEDIO[m]}
                    tamano={24}
                    className={elegido ? 'text-primary-700' : 'text-stone-500'}
                  />
                  {MEDIO_COBRO_ETIQUETA[m]}
                </span>
                <span className="text-sm leading-snug text-stone-600">{AYUDA_MEDIO[m]}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <Aviso tono="peligro">Tu negocio no tiene formas de pago habilitadas. Consultá con la distribuidora.</Aviso>
      )}
    </>
  );
}

function HojaVaciar({ abierta, onCerrar }: { abierta: boolean; onCerrar: () => void }) {
  return (
    <HojaInferior abierta={abierta} onCerrar={onCerrar} titulo="¿Vaciar el carrito?">
      <p className="text-[17px] text-stone-700">Se sacan todos los productos que elegiste.</p>
      <div className="mt-5 flex flex-col gap-3">
        <Boton
          variante="peligro"
          bloque
          tamano="lg"
          icono="delete_sweep"
          onClick={() => {
            vaciarCarrito();
            onCerrar();
          }}
        >
          Sí, vaciar
        </Boton>
        <Boton variante="secundario" bloque tamano="lg" onClick={onCerrar}>
          No, dejarlo
        </Boton>
      </div>
    </HojaInferior>
  );
}

const primeraMayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Un producto del carrito: foto, nombre, precio por unidad, − cantidad +, papelera y subtotal. */
function FilaCarrito({
  l,
  v,
  problema,
  cambio,
  mostrarPrecio,
  permitirSinStock,
}: {
  l: LineaCarrito;
  v: LineaRevisada | undefined;
  problema: LineaRevisada['problema'];
  cambio: CambioPrecio | undefined;
  mostrarPrecio: boolean;
  permitirSinStock: boolean;
}) {
  const nombre = l.nombre ?? v?.nombre ?? 'Producto';
  const precio = v?.precio_bulto ?? l.precio_visto;
  const unidad = l.unidad ?? v?.unidad ?? null;
  const tope = !permitirSinStock && v?.stock_bultos != null && v.stock_bultos > 0 ? v.stock_bultos : null;
  const arreglo = problema && v ? arregloDe(v) : null;
  // Tocar la cantidad acepta el precio de hoy (deja de avisar que cambió).
  const cambiar = (n: number) =>
    cambiarCarrito((c) => fijarBultos(c, { id: l.articulo_id, precio_bulto: v?.precio_bulto ?? l.precio_visto }, n));
  const sacar = () => cambiarCarrito((c) => quitar(c, l.articulo_id));
  const ficha = `/producto/${encodeURIComponent(l.articulo_id)}`;
  return (
    <li
      className={cx(
        'rounded-2xl border bg-white p-3 shadow-sm',
        problema ? 'border-rose-300 bg-rose-50/60' : 'border-stone-200',
      )}
    >
      <div className="flex gap-3">
        <Link to={ficha} className="w-18 shrink-0 self-start" aria-label={`Ver ${nombre}`}>
          <FotoProducto
            chica
            a={{
              nombre,
              categoria: l.categoria ?? v?.categoria ?? null,
              foto_url: null,
              foto_mini_url: v?.foto_mini_url ?? l.foto_mini_url ?? null,
              se_puede_pedir: !problema,
            }}
          />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <h3 className="line-clamp-2 min-w-0 flex-1 text-[17px] leading-snug font-semibold text-stone-900">
              <Link to={ficha} className="hover:underline">
                {nombre}
              </Link>
            </h3>
            {/* El único botón para sacar el producto: el − del control se queda en 1. */}
            <button
              type="button"
              onClick={sacar}
              className="-mt-1 -mr-1 grid h-11 w-11 shrink-0 place-items-center rounded-full text-stone-600 hover:bg-rose-50 hover:text-rose-700 focus-visible:outline-2 focus-visible:outline-primary-600"
              aria-label={`Quitar ${nombre} del carrito`}
            >
              <Icono nombre="delete" tamano={24} />
            </button>
          </div>
          <p className="text-[15px] text-stone-600">
            {mostrarPrecio && precio != null
              ? `${formatoGs(precio)} c/u · ${presentacion(unidad, l.unidades_por_bulto ?? v?.unidades_por_bulto)}`
              : presentacion(unidad, l.unidades_por_bulto ?? v?.unidades_por_bulto)}
          </p>
          {problema ? (
            <div className="mt-2 flex flex-wrap items-center gap-2" role="alert">
              <span className="inline-flex items-center gap-1 text-[15px] font-semibold text-rose-800">
                <Icono nombre="error" tamano={20} relleno />
                {problema === 'sin_stock'
                  ? 'Sin stock por ahora'
                  : problema === 'stock_insuficiente'
                    ? `Solo hay ${cantidadEnUnidad(v?.stock_bultos ?? 0, unidad)}`
                    : problema === 'sin_precio'
                      ? 'No está en tu lista de precios'
                      : 'Ya no está disponible'}
              </span>
              {arreglo?.tipo === 'ajustar' ? (
                <Boton variante="peligro" tamano="sm" className="h-11!" onClick={() => cambiar(arreglo.bultos)}>
                  {`Dejar ${arreglo.bultos}`}
                </Boton>
              ) : null}
            </div>
          ) : null}
          {cambio && mostrarPrecio && !problema ? (
            <p className="mt-1.5 flex items-center gap-1 text-[15px] font-medium text-amber-900">
              <Icono nombre={cambio.ahora > cambio.antes ? 'trending_up' : 'trending_down'} tamano={20} />
              {cambio.ahora > cambio.antes ? 'Subió' : 'Bajó'} de precio: antes {formatoGs(cambio.antes)}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <ControlCantidad valor={l.bultos} onCambiar={cambiar} max={tope} nombre={nombre} compacto minimo={1} />
            {mostrarPrecio && precio != null ? (
              <span className="text-lg font-bold tabular-nums text-stone-900">{formatoGs(precio * l.bultos)}</span>
            ) : (
              <span className="text-[15px] text-stone-600">
                {cantidadEnUnidad(l.bultos, unidad)}
                {mostrarPrecio ? null : ` · ${nombreUnidad(unidad)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function Opciones<T extends string>({
  nombre,
  valor,
  onCambiar,
  opciones,
}: {
  nombre: string;
  valor: T | null;
  onCambiar: (v: T) => void;
  opciones: Array<{ valor: T; titulo: string; ayuda?: string; icono: string }>;
}) {
  const id = useId();
  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {opciones.map((o) => {
        const elegida = o.valor === valor;
        return (
          <label
            key={o.valor}
            className={cx(
              'flex min-h-16 cursor-pointer items-center gap-3 rounded-2xl border-2 bg-white px-4 py-3 transition-colors',
              'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary-600',
              elegida ? 'border-primary-600 bg-primary-50' : 'border-stone-200 hover:border-stone-300',
            )}
          >
            <input
              type="radio"
              name={`${nombre}-${id}`}
              value={o.valor}
              checked={elegida}
              onChange={() => onCambiar(o.valor)}
              className="sr-only"
            />
            <Icono nombre={o.icono} tamano={28} className={elegida ? 'text-primary-700' : 'text-stone-500'} />
            <span className="min-w-0 flex-1 leading-snug">
              <span className="block text-[17px] font-semibold text-stone-900">{o.titulo}</span>
              {o.ayuda ? <span className="block text-[15px] text-stone-600">{o.ayuda}</span> : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function Observaciones({ valor, onCambiar }: { valor: string; onCambiar: (v: string) => void }) {
  const id = useId();
  return (
    <>
      <label htmlFor={id} id="observaciones" className="mb-2 block text-xl font-bold text-stone-900">
        Algo para avisar <span className="text-base font-normal text-stone-600">(si querés)</span>
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={500}
        value={valor}
        onChange={(e) => onCambiar(e.target.value)}
        placeholder="Por ejemplo: dejar en el depósito del fondo, o llamar antes de venir"
        className="w-full rounded-2xl border-2 border-stone-200 bg-white px-4 py-3 text-[17px] placeholder:text-stone-500 focus:border-primary-600 focus:ring-4 focus:ring-primary-200 focus:outline-none"
      />
    </>
  );
}
